import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { EyeIcon, EyeSlashIcon, UserPlusIcon, FingerPrintIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import packageJson from '../../package.json';

export default function Login() {
  const location = useLocation();
  const [form, setForm] = useState({
    email: location.state?.email || '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const { user, setUser, isCheckingToken, setBASE_URL } = useContext(AuthContext);
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();

  // API URL direkt aus Environment oder Fallback
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  // Club Register ID aus .env - nur wenn explizit gesetzt
  const CLUB_REGISTER_ID = process.env.REACT_APP_CLUB_REGISTER_ID;

  // Weiterleitung zum Kalender, wenn ein gültiger Token existiert
  useEffect(() => {
    if (user && !isCheckingToken) {
      console.log('[Login.tsx] Gültiger Token gefunden, navigiere zu /calendar');
      navigate('/calendar', { replace: true });
    }
  }, [user, isCheckingToken, navigate]);

  // Zeige Ladezustand während der Token-Prüfung
  if (isCheckingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Prüfe Token...</p>
        </div>
      </div>
    );
  }

  // Direkter Login beim Backend
  const performLogin = async (email: string, password: string) => {
    setIsLoggingIn(true);
    setError('');

    try {
      console.log('[Login.tsx] Starte direkten Login für:', email);

      const response = await axios.post(`${API_URL}/api/app-auth/login`, {
        email,
        password,
        platform: 'web',
        version: packageJson.version
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': `web-${Date.now()}`
        }
      });

      console.log('[Login.tsx] Login Response:', response.status);

      if (response.data.success) {
        // JWT-Token speichern
        localStorage.setItem('jwt_token', response.data.token);
        console.log('[Login.tsx] JWT-Token gespeichert');

        // API URL speichern für Session-Persistenz
        localStorage.setItem('club_url', API_URL);
        console.log('[Login.tsx] API URL gespeichert:', API_URL);

        // BASE_URL im Context setzen
        setBASE_URL(API_URL);

        // User-Daten setzen
        setUser(response.data.user);
        console.log('[Login.tsx] User-Daten gesetzt:', response.data.user.email);

        // Debug: Zeige alle gespeicherten Werte
        console.log('[Login.tsx] localStorage Inhalt:', {
          jwt_token: localStorage.getItem('jwt_token')?.substring(0, 20) + '...',
          club_url: localStorage.getItem('club_url')
        });

        setIsLoggingIn(false);
        navigate('/calendar', { replace: true });
      } else {
        setError(response.data.message || 'Login fehlgeschlagen.');
        setIsLoggingIn(false);
      }
    } catch (error: any) {
      console.error('[Login.tsx] Fehler beim Login:', error.response?.data || error.message);

      if (error.response?.status === 401) {
        setError('Ungültige Anmeldedaten. Bitte überprüfe E-Mail und Passwort.');
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        setError('Server nicht erreichbar. Bitte wenden Sie sich an den Administrator.');
      } else {
        setError('Login fehlgeschlagen. Bitte überprüfe deine Eingaben.');
      }
      setIsLoggingIn(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await performLogin(form.email, form.password);
  };

  // Passkey-Login (WebAuthn)
  const handlePasskeyLogin = async () => {
    setError('');
    setIsLoggingIn(true);
    try {
      // Optional: Wenn email-Feld gefüllt → allowCredentials für diesen User
      const beginRes = await axios.post(`${API_URL}/api/webauthn/login/begin`, {
        email: form.email || undefined
      });
      const credential = await startAuthentication({ optionsJSON: beginRes.data });
      const completeRes = await axios.post(`${API_URL}/api/webauthn/login/complete`, {
        email: form.email || undefined,
        credential
      });
      if (completeRes.data.success && completeRes.data.token) {
        localStorage.setItem('jwt_token', completeRes.data.token);
        localStorage.setItem('club_url', API_URL);
        setBASE_URL(API_URL);
        setUser(completeRes.data.user);
        setIsLoggingIn(false);
        navigate('/calendar', { replace: true });
      } else {
        setError(completeRes.data.error || 'Passkey-Login fehlgeschlagen.');
        setIsLoggingIn(false);
      }
    } catch (err: any) {
      console.error('[Login.tsx] Passkey-Fehler:', err);
      // Browser-Abbruch (User Cancel) lautlos behandeln
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        setIsLoggingIn(false);
        return;
      }
      setError(err?.response?.data?.error || err?.message || 'Passkey-Login nicht möglich.');
      setIsLoggingIn(false);
    }
  };

  const passkeySupported = browserSupportsWebAuthn();

  const loginAsDemo = async (role: string) => {
    const credentials: Record<string, { email: string; password: string }> = {
      vorstand: {
        email: process.env.REACT_APP_DEMO_VORSTAND_EMAIL || 'vorstand@example.com',
        password: process.env.REACT_APP_DEMO_VORSTAND_PASSWORD || 'demo123',
      },
      trainer: {
        email: process.env.REACT_APP_DEMO_TRAINER_EMAIL || 'trainer@example.com',
        password: process.env.REACT_APP_DEMO_TRAINER_PASSWORD || 'demo123',
      },
      mitglied: {
        email: process.env.REACT_APP_DEMO_MITGLIED_EMAIL || 'mitglied@example.com',
        password: process.env.REACT_APP_DEMO_MITGLIED_PASSWORD || 'demo123',
      },
      gast: {
        email: process.env.REACT_APP_DEMO_GAST_EMAIL || 'gast@example.com',
        password: process.env.REACT_APP_DEMO_GAST_PASSWORD || 'demo123',
      }
    };

    const creds = credentials[role];
    if (creds) {
      setForm(creds);
      await performLogin(creds.email, creds.password);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0f1419] relative px-4">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gray-50 dark:bg-[#0f1419] pointer-events-none" />

      <div className="relative w-full max-w-md">
        {/* Main Card */}
        <div className="bg-white dark:bg-[#1c2432] rounded-3xl shadow-2xl shadow-gray-200/50 dark:shadow-black/40 border border-gray-100 dark:border-gray-800/50 p-10">

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">
              Anmelden
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Willkommen zurück
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                E-Mail
              </label>
              <input
                type="email"
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all duration-200"
                placeholder="name@beispiel.de"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Passwort
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all duration-200 pr-12"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-error-50 dark:bg-error-900/20 text-error-600 dark:text-error-400 rounded-xl text-sm font-medium">
                {error}
              </div>
            )}

            {/* Forgot Password Link */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors"
              >
                Passwort vergessen?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3.5 rounded-xl font-medium transition-all duration-200 hover:shadow-lg hover:shadow-primary-600/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none active:scale-[0.98]"
            >
              {isLoggingIn ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Anmelden...
                </span>
              ) : 'Anmelden'}
            </button>

            {/* Passkey Login */}
            {passkeySupported && (
              <button
                type="button"
                disabled={isLoggingIn}
                onClick={handlePasskeyLogin}
                className="w-full flex items-center justify-center gap-2 bg-transparent border border-gray-200 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-[#0f1419] transition-all duration-200 font-medium disabled:opacity-50"
              >
                <FingerPrintIcon className="h-5 w-5" />
                Mit Passkey anmelden
              </button>
            )}

            {/* Divider & Register */}
            {CLUB_REGISTER_ID && (
              <div className="pt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200 dark:border-gray-700/50"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 text-xs text-gray-400 dark:text-gray-500 bg-white dark:bg-[#1c2432] uppercase tracking-wider">
                      oder
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="w-full mt-6 flex items-center justify-center gap-2 bg-transparent border border-gray-200 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 py-3.5 rounded-xl hover:bg-gray-50 dark:hover:bg-[#0f1419] transition-all duration-200 font-medium"
                >
                  <UserPlusIcon className="h-5 w-5" />
                  Konto erstellen
                </button>
              </div>
            )}

            {/* Demo Buttons */}
            {process.env.REACT_APP_DEMO === 'true' && (
              <div className="pt-6 mt-2">
                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200 dark:border-gray-700/50"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 text-xs text-gray-400 dark:text-gray-500 bg-white dark:bg-[#1c2432] uppercase tracking-wider">
                      Demo
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    disabled={isLoggingIn}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-gray-50 dark:bg-[#0f1419] text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-100 dark:hover:bg-[#1a2332] transition-all duration-200 disabled:opacity-50"
                    onClick={() => loginAsDemo('vorstand')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    Vorstand
                  </button>
                  <button
                    type="button"
                    disabled={isLoggingIn}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-gray-50 dark:bg-[#0f1419] text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-100 dark:hover:bg-[#1a2332] transition-all duration-200 disabled:opacity-50"
                    onClick={() => loginAsDemo('trainer')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Trainer
                  </button>
                  <button
                    type="button"
                    disabled={isLoggingIn}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-gray-50 dark:bg-[#0f1419] text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-100 dark:hover:bg-[#1a2332] transition-all duration-200 disabled:opacity-50"
                    onClick={() => loginAsDemo('mitglied')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Mitglied
                  </button>
                  <button
                    type="button"
                    disabled={isLoggingIn}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-gray-50 dark:bg-[#0f1419] text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-100 dark:hover:bg-[#1a2332] transition-all duration-200 disabled:opacity-50"
                    onClick={() => loginAsDemo('gast')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Gast
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Version Footer */}
        <p className="text-center mt-8 text-xs text-gray-400 dark:text-gray-600">
          v{packageJson.version}
        </p>
      </div>
    </div>
  );
}
